import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth, usePermission } from '@saas-kit/lib';
import {
  ErrorBanner,
  PageHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@saas-kit/ui';

import { TenantRequiredState } from '../../components/TenantRequiredState';
import {
  createCategory,
  createCounterGroup,
  deleteCategory,
  deleteCounterGroup,
  listCategories,
  listCounterGroups,
  updateCategory,
  updateCounterGroup,
} from './api';
import { BrandingTab } from './BrandingTab';
import { CountersTab } from './CountersTab';
import { CustomerTypesTab } from './CustomerTypesTab';
import { ServicesTab } from './ServicesTab';
import { SimpleCatalogTab } from './SimpleCatalogTab';

export function QmsSetupPage(): JSX.Element {
  const { t } = useTranslation('qms');
  const selectedTenant = useAuth((s) => s.selectedTenant);
  const canViewCatalog = usePermission('qms.catalog.view');
  const canManageCatalog = usePermission('qms.catalog.manage');
  const canViewCounters = usePermission('qms.counters.view');
  const canManageCounters = usePermission('qms.counters.manage');
  const canManageBranding = usePermission('qms.branding.manage');

  // Only surface tabs the actor can use — hide the trigger entirely rather
  // than rendering a permission error inside a visible tab.
  const availableTabs = useMemo(() => {
    const ids: string[] = [];
    if (canViewCatalog) ids.push('customer_types', 'categories', 'services');
    if (canViewCounters) ids.push('counter_groups', 'counters');
    if (canManageBranding) ids.push('branding');
    return ids;
  }, [canViewCatalog, canViewCounters, canManageBranding]);

  const [tab, setTab] = useState(() => availableTabs[0] ?? '');

  if (!selectedTenant) {
    return (
      <TenantRequiredState
        title={t('tenant_required.title')}
        body={t('tenant_required.body')}
        cta={t('tenant_required.cta')}
      />
    );
  }

  if (availableTabs.length === 0) {
    return <ErrorBanner>{t('errors.view_denied')}</ErrorBanner>;
  }

  const activeTab = availableTabs.includes(tab) ? tab : availableTabs[0]!;

  return (
    <div className="space-y-6">
      <PageHeader title={t('setup.title')} description={t('setup.subtitle')} />

      <Tabs value={activeTab} onValueChange={setTab}>
        <TabsList>
          {canViewCatalog ? (
            <>
              <TabsTrigger value="customer_types">
                {t('tabs.customer_types')}
              </TabsTrigger>
              <TabsTrigger value="categories">
                {t('tabs.categories')}
              </TabsTrigger>
              <TabsTrigger value="services">{t('tabs.services')}</TabsTrigger>
            </>
          ) : null}
          {canViewCounters ? (
            <>
              <TabsTrigger value="counter_groups">
                {t('tabs.counter_groups')}
              </TabsTrigger>
              <TabsTrigger value="counters">{t('tabs.counters')}</TabsTrigger>
            </>
          ) : null}
          {canManageBranding ? (
            <TabsTrigger value="branding">{t('tabs.branding')}</TabsTrigger>
          ) : null}
        </TabsList>

        {canViewCatalog ? (
          <>
            <TabsContent value="customer_types">
              <CustomerTypesTab
                canManage={canManageCatalog}
                enabled={canViewCatalog}
              />
            </TabsContent>
            <TabsContent value="categories">
              <SimpleCatalogTab
                cacheKey="qms-categories"
                kind="category"
                canManage={canManageCatalog}
                enabled={canViewCatalog}
                list={listCategories}
                create={createCategory}
                update={updateCategory}
                remove={deleteCategory}
              />
            </TabsContent>
            <TabsContent value="services">
              <ServicesTab
                canManage={canManageCatalog}
                enabled={canViewCatalog}
              />
            </TabsContent>
          </>
        ) : null}

        {canViewCounters ? (
          <>
            <TabsContent value="counter_groups">
              <SimpleCatalogTab
                cacheKey="qms-counter-groups"
                kind="counter_group"
                canManage={canManageCounters}
                enabled={canViewCounters}
                list={listCounterGroups}
                create={createCounterGroup}
                update={updateCounterGroup}
                remove={deleteCounterGroup}
              />
            </TabsContent>
            <TabsContent value="counters">
              <CountersTab
                canManage={canManageCounters}
                enabled={canViewCounters}
              />
            </TabsContent>
          </>
        ) : null}

        {canManageBranding ? (
          <TabsContent value="branding">
            <BrandingTab />
          </TabsContent>
        ) : null}
      </Tabs>
    </div>
  );
}
